import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Heart, MapPin, Shield, HeartPulse, BookOpen, Sprout, ArrowUpRight } from "lucide-react";

const CAMPUS = "https://customer-assets-wrfwihn1.emergentagent.net/job_ethiopia-protect/artifacts/ft4sbtkw_file_00000000e9dc81fd9cbd9fa2ca699169.png";
const VIRTUAL_TOUR = "https://customer-assets-wrfwihn1.emergentagent.net/job_ethiopia-protect/artifacts/6py0wyla_file_00000000024881fdb52b4b6942d7648c.png";
const MASTER_PLAN = "https://customer-assets-wrfwihn1.emergentagent.net/job_ethiopia-protect/artifacts/h5974r1z_file_000000001c6c81fdb2783b4f7138cfd5.png";

export default function FutureCampusPage() {
  const [donate, setDonate] = useState("");
  useEffect(() => { api.get("/settings").then(({ data }) => setDonate(data.gofundme_url)).catch(() => {}); }, []);

  return (
    <div data-testid="future-campus-page">
      <section className="relative min-h-[55vh] flex items-end overflow-hidden">
        <img src={CAMPUS} alt="Future campus" className="absolute inset-0 w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1F4D2E] via-[#1F4D2E]/40 to-transparent" />
        <div className="relative max-w-7xl mx-auto w-full px-6 lg:px-10 pb-14">
          <p className="text-xs tracking-[0.25em] uppercase font-bold text-[#E0A526] mb-3 flex items-center gap-2"><MapPin size={14} /> Future Headquarters Campus</p>
          <h1 className="font-display text-5xl sm:text-6xl font-bold text-white tracking-tight">A Safe Haven. A Path Forward.</h1>
          <p className="text-lg text-white/90 mt-4 max-w-2xl">A world-class facility in the capital city — providing protection, healing, education, healthcare, life skills and opportunity for vulnerable children and families.</p>
        </div>
      </section>

      {/* INTRO + FACTS */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <div className="max-w-3xl">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#C1272D] mb-3">The master plan</p>
          <h2 className="font-display text-4xl sm:text-5xl font-semibold text-[#1F4D2E] leading-tight">A comprehensive campus for hundreds of children.</h2>
          <p className="text-lg text-[#4F6B54] leading-relaxed mt-5">Our future headquarters brings every part of a child's life onto one safe, self-reliant campus — family-style homes, a school and computer center, a health clinic, counseling, a Mercy Made workshop, playgrounds and community gardens.</p>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-6 mt-12">
          {[{ n: "12.5", l: "Acres of land (target)" }, { n: "23+", l: "Buildings planned" }, { n: "500+", l: "Children (Phase 2)" }, { n: "500+", l: "Families served monthly" }].map((s) => (
            <div key={s.l} className="border-l-2 border-[#E0A526] pl-4">
              <p className="font-display text-4xl font-bold text-[#1F4D2E]">{s.n}</p>
              <p className="text-sm text-[#4F6B54] mt-1">{s.l}</p>
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mt-12">
          {[{ icon: Shield, t: "Protect", d: "Safe homes & care" }, { icon: HeartPulse, t: "Heal", d: "Clinic & counseling" }, { icon: BookOpen, t: "Educate", d: "School & tech center" }, { icon: Sprout, t: "Empower", d: "Skills & Mercy Made" }].map((p) => (
            <div key={p.t} className="bg-[#E7EDE2] p-6 rounded-sm">
              <p.icon className="text-[#C1272D] mb-3" size={24} />
              <h3 className="font-display text-xl font-semibold text-[#1F4D2E]">{p.t}</h3>
              <p className="text-sm text-[#4F6B54] mt-1">{p.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* MASTER PLAN VISUALS */}
      <section className="max-w-7xl mx-auto px-6 lg:px-10 pb-8 space-y-10">
        {[{ img: VIRTUAL_TOUR, label: "Virtual Tour — Future Headquarters Campus" }, { img: MASTER_PLAN, label: "Future Headquarters — Master Plan" }].map((v) => (
          <div key={v.label} data-testid="campus-plan">
            <div className="flex items-center justify-between mb-3 gap-4">
              <h3 className="font-display text-2xl font-semibold text-[#1F4D2E]">{v.label}</h3>
              <a href={v.img} target="_blank" rel="noopener noreferrer" className="shrink-0 text-sm font-semibold text-[#C1272D] inline-flex items-center gap-1 hover:underline">Open full size <ArrowUpRight size={15} /></a>
            </div>
            <a href={v.img} target="_blank" rel="noopener noreferrer" className="block border border-[#1F4D2E]/10 overflow-hidden shadow-[0_8px_30px_rgb(0,0,0,0.06)]">
              <img src={v.img} alt={v.label} className="w-full h-auto" />
            </a>
          </div>
        ))}
      </section>

      {/* PHASES */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 py-20">
        <h2 className="font-display text-3xl sm:text-4xl font-semibold text-[#1F4D2E] mb-10">How we get there — our building phases.</h2>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {[{ p: "Phase 1", t: "Rented Location (Now)", d: "Rent a suitable property, open the Girls' Home, start core programs and stabilize funding." }, { p: "Phase 2", t: "Land & Design (Next)", d: "Secure land, complete architectural design, permits and detailed cost estimates." }, { p: "Phase 3", t: "Construction (Future)", d: "Build the permanent campus in phases, expand services and open the community center." }].map((ph) => (
            <div key={ph.p} className="bg-white border border-[#1F4D2E]/10 p-7 relative">
              <span className="absolute -top-3 left-7 bg-[#C1272D] text-white text-xs font-bold px-3 py-1 rounded-full">{ph.p}</span>
              <h3 className="font-display text-xl font-semibold text-[#1F4D2E] mt-3 mb-2">{ph.t}</h3>
              <p className="text-sm text-[#4F6B54] leading-relaxed">{ph.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* BUILD A PIECE */}
      <section className="max-w-6xl mx-auto px-6 lg:px-10 pb-24">
        <div className="bg-[#1F4D2E] text-white p-10 md:p-14">
          <p className="text-xs tracking-[0.2em] uppercase font-bold text-[#E0A526] mb-3">Build a piece of the refuge</p>
          <h2 className="font-display text-3xl sm:text-4xl font-semibold mb-3">Name a room, building or area that you fund.</h2>
          <p className="text-white/80 max-w-2xl mb-8">Your legacy can inspire generations — name a counseling room, computer lab or family home in honor of someone you love. Join the Refugium Village of monthly givers as we begin building.</p>
          <div className="flex flex-wrap gap-3 mb-8">
            {["$10 Village Friend", "$25 Village Builder", "$50 Village Protector", "$100 Village Champion", "$250 Village Guardian", "$500+ Founding Circle"].map((t) => (
              <span key={t} className="bg-white/10 border border-white/15 rounded-full px-4 py-2 text-sm font-semibold">{t}</span>
            ))}
          </div>
          <div className="flex flex-wrap gap-4">
            <a href={donate || "https://gofund.me/15d8987b7"} target="_blank" rel="noopener noreferrer" data-testid="campus-donate-btn" className="bg-[#C1272D] text-white rounded-full px-8 py-4 font-semibold hover:bg-[#9E1F24] transition-colors inline-flex items-center gap-2"><Heart size={18} fill="white" /> Help build the refuge</a>
            <Link to="/projects" className="border border-white/40 text-white rounded-full px-8 py-4 font-semibold hover:bg-white hover:text-[#1F4D2E] transition-colors" data-testid="campus-projects-btn">View active projects</Link>
          </div>
        </div>
      </section>
    </div>
  );
}
